import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, Pencil, X } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';
import { TriInput } from './MissionsManager';

const EMPTY = {
  name_fr: '', name_en: '', name_zh: '',
  origin_fr: '', origin_en: '', origin_zh: '',
  quantity_fr: '', quantity_en: '', quantity_zh: '',
  category_fr: '', category_en: '', category_zh: '',
  discount: '',
};

export default function RescuedManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get('/admin/rescued-products'); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const startEdit = (r) => {
    setEditing(r.id);
    setForm({
      name_fr: r.name.fr, name_en: r.name.en, name_zh: r.name.zh,
      origin_fr: r.origin.fr, origin_en: r.origin.en, origin_zh: r.origin.zh,
      quantity_fr: r.quantity.fr, quantity_en: r.quantity.en, quantity_zh: r.quantity.zh,
      category_fr: r.category.fr, category_en: r.category.en, category_zh: r.category.zh,
      discount: r.discount,
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === 'new') await api.post('/admin/rescued-products', form);
      else await api.put(`/admin/rescued-products/${editing}`, form);
      toast({ title: 'Produit enregistré' });
      cancel(); load();
    } catch (err) {
      console.error('RescuedManager save error', err?.response || err);
      const detail = err?.response?.data?.detail;
      const detailStr = Array.isArray(detail) ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(' • ') : (detail || err?.message || 'Impossible');
      toast({ title: `Erreur ${err?.response?.status || ''}`.trim(), description: detailStr, duration: 15000 });
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    await api.delete(`/admin/rescued-products/${id}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-brand-navy">Produits rescapés ({items.length})</h2>
          <button onClick={() => { setEditing('new'); setForm(EMPTY); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-brand-navy text-white text-xs font-semibold">
            <Plus className="w-3.5 h-3.5" /> Nouveau
          </button>
        </div>
        {items.length === 0 ? <p className="text-sm text-gray-400 italic">Aucun.</p> : (
          <ul className="space-y-2 max-h-[500px] overflow-y-auto">
            {items.map((r) => (
              <li key={r.id} className="p-3 rounded border border-gray-100 flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-brand-navy text-sm">{r.name.fr}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{r.origin.fr} • {r.quantity.fr}</div>
                  <div className="text-[10px] text-gray-400 mt-1">{r.category.fr} • {r.discount}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEdit(r)} className="p-1.5 rounded hover:bg-gray-100"><Pencil className="w-4 h-4 text-brand-navy" /></button>
                  <button onClick={() => del(r.id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-brand-navy">{editing === 'new' ? 'Nouveau produit' : editing ? 'Modifier' : 'Sélectionner'}</h2>
          {editing && <button onClick={cancel} className="text-xs text-gray-500 flex items-center gap-1"><X className="w-3.5 h-3.5" /> Annuler</button>}
        </div>
        {!editing ? <p className="text-sm text-gray-400 italic">Sélectionnez ou créez un produit.</p> : (
          <form onSubmit={save} className="space-y-3">
            <TriInput label="Nom du produit" fr={form.name_fr} en={form.name_en} zh={form.name_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['name_fr', 'name_en', 'name_zh']} />
            <TriInput label="Origine" fr={form.origin_fr} en={form.origin_en} zh={form.origin_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['origin_fr', 'origin_en', 'origin_zh']} />
            <TriInput label="Quantité" fr={form.quantity_fr} en={form.quantity_en} zh={form.quantity_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['quantity_fr', 'quantity_en', 'quantity_zh']} />
            <TriInput label="Catégorie" fr={form.category_fr} en={form.category_en} zh={form.category_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['category_fr', 'category_en', 'category_zh']} />
            <div>
              <label className="block text-xs font-semibold text-brand-navy uppercase mb-1">Remise (ex: -45%)</label>
              <input value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })}
                placeholder="-45%" className="w-full px-3 py-2 border border-gray-200 rounded text-sm" />
            </div>
            <button disabled={busy} className="btn-yellow w-full justify-center disabled:opacity-60"><Save className="w-4 h-4" /> Enregistrer</button>
          </form>
        )}
      </div>
    </div>
  );
}
